import useStatusPill from '@hooks/useStatusPill'
import { AnimatePresence, motion, type Variants } from 'framer-motion'
import React from 'react'

const pillVariants: Variants = {
  hidden: { opacity: 0, scale: 0.92 },
  shown: { opacity: 1, scale: 1 },
}

// The badge lands as a dot, swells into the green circle, then the check draws in.
const badgeVariants: Variants = {
  hidden: { scale: 0.25 },
  shown: { scale: 1, transition: { type: 'spring', bounce: 0.45, duration: 0.5, delay: 0.1 } },
}

const checkVariants: Variants = {
  hidden: { pathLength: 0 },
  shown: { pathLength: 1, transition: { duration: 0.25, delay: 0.3, ease: 'easeOut' } },
}

/** Floats over the header row of the content column; RootLayout gives it a positioned parent. */
export const StatusPill = () => {
  const { status } = useStatusPill()

  return (
    <div className="pointer-events-none absolute inset-x-0 top-0 z-[150] flex justify-center px-4 sm:top-3">
      <AnimatePresence>
        {status && (
          <motion.div
            key={status.id}
            role="status"
            variants={pillVariants}
            initial="hidden"
            animate="shown"
            exit="hidden"
            transition={{ type: 'spring', bounce: 0.3, duration: 0.4 }}
            className="flex h-10 max-w-full min-w-0 items-center gap-2.25 rounded-pill border border-solid border-hairline-strong bg-bg/85 pr-4 pl-2.25 font-text text-[15px] font-semibold whitespace-nowrap text-fg shadow-md backdrop-blur-xl"
          >
            <motion.span
              aria-hidden
              variants={badgeVariants}
              className="grid size-[22px] shrink-0 place-items-center rounded-full bg-success text-bg"
            >
              <svg viewBox="0 0 20 20" className="size-[15px]">
                <motion.path
                  variants={checkVariants}
                  d="M5 10.5l3.25 3.25L15 6.75"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2.5}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </motion.span>
            <span className="min-w-0 truncate">{status.label}</span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
